/**
 * BlockingErrorState Component
 *
 * Full-page error state for critical failures that require user action.
 * Implements UX Intent pattern: error_handling: blocking
 *
 * Features:
 * - Full-page error display with clear message
 * - Multiple recovery actions
 * - Optional support contact
 * - Error code display for support
 * - Retry functionality
 *
 * Used by: SCR-803 (Forgot Password), SCR-804 (Change Password),
 *          SCR-805 (2FA Setup), SCR-903 (Payment Methods),
 *          SCR-904 (Billing History), SCR-905 (Cancel Subscription)
 */

import type { ReactNode } from 'react';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ShieldExclamationIcon,
  ServerIcon,
  WifiIcon,
  LockClosedIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';
import { Button } from '../Button';

export type BlockingErrorVariant =
  | 'general'
  | 'server'
  | 'network'
  | 'auth'
  | 'permission'
  | 'payment'
  | 'validation';

export interface BlockingErrorAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick?: () => void;
  /** Link href (use instead of onClick for navigation) */
  href?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline';
  /** Whether this is the primary action */
  primary?: boolean;
}

export interface BlockingErrorStateProps {
  /** Error title */
  title: string;
  /** Error description */
  description: string;
  /** Error variant determines icon and styling */
  variant?: BlockingErrorVariant;
  /** Custom icon */
  icon?: ReactNode;
  /** Recovery actions */
  actions?: BlockingErrorAction[];
  /** Error code for support reference */
  errorCode?: string;
  /** Whether to show contact support link */
  showSupportLink?: boolean;
  /** Support contact URL */
  supportUrl?: string;
  /** Support contact label */
  supportLabel?: string;
  /** Additional content below the error message */
  children?: ReactNode;
  /** Custom class name for the container */
  className?: string;
  /** Use dark theme (for dark backgrounds) */
  dark?: boolean;
}

const variantConfig = {
  general: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
  },
  server: {
    icon: ServerIcon,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
  },
  network: {
    icon: WifiIcon,
    iconColor: 'text-mv-blue-500',
    iconBg: 'bg-mv-blue-100 dark:bg-mv-blue-900/30',
  },
  auth: {
    icon: LockClosedIcon,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
  },
  permission: {
    icon: ShieldExclamationIcon,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
  },
  payment: {
    icon: CreditCardIcon,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
  },
  validation: {
    icon: ExclamationCircleIcon,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
  },
};

export function BlockingErrorState({
  title,
  description,
  variant = 'general',
  icon,
  actions = [],
  errorCode,
  showSupportLink = false,
  supportUrl = '/help/contact',
  supportLabel = 'Neem contact op met support',
  children,
  className,
  dark = false,
}: BlockingErrorStateProps) {
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  // Ensure there's at least one action or show default
  const displayActions =
    actions.length > 0
      ? actions
      : [
          {
            label: 'Probeer opnieuw',
            onClick: () => window.location.reload(),
            primary: true,
          },
        ];

  return (
    <div
      className={cn(
        'flex min-h-[400px] flex-col items-center justify-center px-6 py-12',
        dark ? 'bg-mv-gray-900' : 'bg-mv-gray-50',
        className
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="mx-auto max-w-md text-center">
        {/* Icon */}
        <div
          className={cn(
            'mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full',
            config.iconBg
          )}
        >
          {icon || <IconComponent className={cn('h-8 w-8', config.iconColor)} />}
        </div>

        {/* Title */}
        <h1
          className={cn(
            'text-2xl font-bold',
            dark ? 'text-white' : 'text-mv-gray-900'
          )}
        >
          {title}
        </h1>

        {/* Description */}
        <p
          className={cn(
            'mt-3 text-base',
            dark ? 'text-mv-gray-400' : 'text-mv-gray-600'
          )}
        >
          {description}
        </p>

        {/* Error code */}
        {errorCode && (
          <p
            className={cn(
              'mt-2 font-mono text-sm',
              dark ? 'text-mv-gray-500' : 'text-mv-gray-400'
            )}
          >
            Foutcode: {errorCode}
          </p>
        )}

        {/* Custom content */}
        {children && <div className="mt-4">{children}</div>}

        {/* Actions */}
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          {displayActions.map((action, index) => {
            const buttonVariant = action.primary
              ? 'primary'
              : action.variant || 'secondary';

            if (action.href) {
              return (
                <a key={index} href={action.href}>
                  <Button variant={buttonVariant}>{action.label}</Button>
                </a>
              );
            }

            return (
              <Button
                key={index}
                variant={buttonVariant}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            );
          })}
        </div>

        {/* Support link */}
        {showSupportLink && (
          <div className="mt-6">
            <a
              href={supportUrl}
              className={cn(
                'text-sm underline',
                dark
                  ? 'text-mv-blue-400 hover:text-mv-blue-300'
                  : 'text-mv-blue-600 hover:text-mv-blue-700'
              )}
            >
              {supportLabel}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pre-configured error states for common scenarios
 */

export function NetworkErrorState({
  onRetry,
  ...props
}: Omit<BlockingErrorStateProps, 'title' | 'description' | 'variant'> & {
  onRetry?: () => void;
}) {
  return (
    <BlockingErrorState
      title="Geen verbinding"
      description="Controleer uw internetverbinding en probeer het opnieuw."
      variant="network"
      actions={[
        {
          label: 'Opnieuw proberen',
          onClick: onRetry || (() => window.location.reload()),
          primary: true,
        },
      ]}
      {...props}
    />
  );
}

export function ServerErrorState({
  onRetry,
  ...props
}: Omit<BlockingErrorStateProps, 'title' | 'description' | 'variant'> & {
  onRetry?: () => void;
}) {
  return (
    <BlockingErrorState
      title="Er is iets misgegaan"
      description="We konden uw verzoek niet verwerken. Probeer het later opnieuw."
      variant="server"
      showSupportLink
      actions={[
        {
          label: 'Opnieuw proberen',
          onClick: onRetry || (() => window.location.reload()),
          primary: true,
        },
        {
          label: 'Terug naar home',
          href: '/',
        },
      ]}
      {...props}
    />
  );
}

export function AuthErrorState({
  onLogin,
  ...props
}: Omit<BlockingErrorStateProps, 'title' | 'description' | 'variant'> & {
  onLogin?: () => void;
}) {
  return (
    <BlockingErrorState
      title="Sessie verlopen"
      description="Uw sessie is verlopen. Log opnieuw in om door te gaan."
      variant="auth"
      actions={[
        {
          label: 'Opnieuw inloggen',
          onClick: onLogin,
          href: onLogin ? undefined : '/login',
          primary: true,
        },
      ]}
      {...props}
    />
  );
}

export function PermissionErrorState(
  props: Omit<BlockingErrorStateProps, 'title' | 'description' | 'variant'>
) {
  return (
    <BlockingErrorState
      title="Geen toegang"
      description="U heeft geen toestemming om deze pagina te bekijken."
      variant="permission"
      actions={[
        {
          label: 'Terug naar home',
          href: '/',
          primary: true,
        },
      ]}
      {...props}
    />
  );
}

export function PaymentErrorState({
  onRetry,
  ...props
}: Omit<BlockingErrorStateProps, 'title' | 'description' | 'variant'> & {
  onRetry?: () => void;
}) {
  return (
    <BlockingErrorState
      title="Betaling mislukt"
      description="Uw betaling kon niet worden verwerkt. Controleer uw gegevens en probeer het opnieuw."
      variant="payment"
      showSupportLink
      actions={[
        {
          label: 'Opnieuw proberen',
          onClick: onRetry,
          primary: true,
        },
        {
          label: 'Andere betaalmethode',
          href: '/settings/billing/payment-methods',
        },
      ]}
      {...props}
    />
  );
}
